import type { AlertCategory, AlertSeverity, AlertSource } from '../types/alert';
import type { CameraLabel, VehicleStatus } from '../types/vehicle';
import type { RiskLevel } from '../types/driver';

// ─── Vehicle Status ───────────────────────────────────────────────
export const VEHICLE_STATUS_LABELS: Record<VehicleStatus, string> = {
  critical_event: 'Evento Crítico',
  no_signal: 'Sin Señal',
  offline: 'Fuera de Servicio',
  stopped: 'Detenido',
  in_route: 'En Ruta',
};

export const VEHICLE_STATUS_COLORS: Record<VehicleStatus, string> = {
  critical_event: '#EF4444',
  no_signal: '#F97316',
  offline: '#6B7280',
  stopped: '#F59E0B',
  in_route: '#10B981',
};

// ─── Alert Severity ───────────────────────────────────────────────
export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  critical: 'Crítica',
  medium: 'Media',
  low: 'Baja',
};

export const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  critical: '#EF4444',
  medium: '#F59E0B',
  low: '#10B981',
};

export const SEVERITY_BG: Record<AlertSeverity, string> = {
  critical: 'rgba(239,68,68,0.12)',
  medium: 'rgba(245,158,11,0.12)',
  low: 'rgba(16,185,129,0.12)',
};

// ─── Alert Category ───────────────────────────────────────────────
export const CATEGORY_LABELS: Record<AlertCategory, string> = {
  collision: 'Colisión / Impacto',
  fatigue: 'Fatiga',
  harsh_acceleration: 'Aceleración Brusca',
  harsh_braking: 'Frenada Brusca',
  harsh_cornering: 'Curva Peligrosa',
  overspeed: 'Exceso de Velocidad',
  overtime_driving: 'Tiempo de Conducción Excedido',
  sos: 'Emergencia (SOS)',
  unknown: 'Sin Clasificar',
};

// ─── Alert Source (BE-45) ─────────────────────────────────────────
// El catálogo dice QUÉ pasó; esto dice QUIÉN lo dijo. Una alerta `human` vale
// lo que valga la palabra de quien la declaró, y el supervisor tiene que poder
// distinguirla de una medida por el sensor antes de decidir qué hacer.
export const ALERT_SOURCE_LABELS: Record<AlertSource, string> = {
  device: 'Detectada por el equipo',
  human: 'Declarada por una persona',
};

// ─── Risk Level ───────────────────────────────────────────────────
export const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Bajo',
  medium: 'Medio',
  high: 'Alto',
};

export const RISK_COLORS: Record<RiskLevel, string> = {
  low: '#10B981',
  medium: '#F59E0B',
  high: '#EF4444',
};

// ─── Camera Label (FE-14, openapi.yaml `CameraLabel`) ──────────────
export const CAMERA_LABEL_LABELS: Record<CameraLabel, string> = {
  FRONT: 'Frontal',
  CABIN: 'Cabina',
  REAR: 'Trasera',
  SIDE_LEFT: 'Lateral izquierda',
  SIDE_RIGHT: 'Lateral derecha',
  OTHER: 'Otra',
};

// ─── App Config ───────────────────────────────────────────────────
export const APP_NAME = 'Paitron Radar';
export const APP_TAGLINE = 'Supervisión Inteligente de Flotas';
export const MAP_CENTER: [number, number] = [4.711, -74.0721]; // Bogotá, Colombia
export const MAP_ZOOM = 11;

// ─── Navigation ───────────────────────────────────────────────────
// FE-27 — mismo corte donde el shell oculta la barra lateral y aparece la
// `bottom-nav` (`styles/index.css`, `@media (max-width: 900px)`). Por debajo, el
// histórico de recorrido va a una página propia en vez del panel flotante.
export const MOBILE_MEDIA_QUERY = '(max-width: 900px)';

export const NAV_ROUTES = {
  LOGIN: '/login',
  // Manejador de acciones de correo de Identity Platform (invitación,
  // recuperación, confirmación). Es el `callbackUri` del proyecto: lo fija
  // `infra/identity/email-config.es.json`. Si cambia aquí, cambia allá.
  AUTH_ACTION: '/auth/action',
  DASHBOARD: '/dashboard',
  MONITORING: '/monitoring',
  // FE-27 — histórico de recorrido en página propia (mobile).
  MONITORING_HISTORY: '/monitoring/history',
  ALERTS: '/alerts',
  DRIVERS: '/drivers',
  VEHICLES: '/vehicles',
  CALENDAR: '/calendar',
  TRIPS: '/trips',
  LOGBOOK: '/logbook',
  EXPENSES: '/expenses',
  // FE-32 — ingresos (los cuatro roles) y el resultado (Admin, Ejecutivo, Supervisor).
  REVENUES: '/revenues',
  FINANCE: '/finance',
  OPERATOR: '/my-performance',
  ADMIN: '/admin',
  REPORTS: '/reports',
} as const;
